const KEY_PREFIX = "IHub-";

/**
 * LocalStorageManager provides utility functions to manage local storage with expiration.
 * It allows storing items with an expiry date, retrieving them, and cleaning up expired items.
 */
class LocalStorageManager {
    static prefixKey(key: string): string {
        return KEY_PREFIX + key;
    }

    /**
     * Stores an item in localStorage with an expiry date.
     * @param key The key under which the item is stored.
     * @param value The value to store.
     * @param days The number of days until the item expires. Default is 7 days.
     */
    static storeWithExpiry(
        key: string,
        value: string | number | object,
        days: number = 7,
    ): void {
        const now = new Date();
        const item = {
            value,
            expiry: now.getTime() + days * 24 * 60 * 60 * 1000,
        };
        localStorage.setItem(this.prefixKey(key), JSON.stringify(item));
    }

    /**
     * Retrieves an item from localStorage with its expiry.
     * If the item is expired, it removes it from localStorage and returns null.
     * @param key The key of the item to retrieve.
     * @returns The value of the item if it exists and is not expired, otherwise null.
     */
    static getWithExpiry(key: string): string | number | object | null {
        const itemStr = localStorage.getItem(this.prefixKey(key));
        if (!itemStr) {
            return null;
        }
        const item = JSON.parse(itemStr);
        const now = new Date();
        if (now.getTime() > item.expiry) {
            localStorage.removeItem(this.prefixKey(key));
            return null;
        }
        return item.value;
    }

    /**
     * Cleans up expired items from localStorage.
     * @returns The number of items that were removed.
     */
    static cleanupExpiredItems(): number {
        const now = new Date().getTime();
        let cleanedCount = 0;

        // Iterate through all localStorage items
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);

            // Only process keys with our prefix
            if (key && key.startsWith(KEY_PREFIX)) {
                try {
                    const itemStr = localStorage.getItem(key);
                    if (itemStr) {
                        const item = JSON.parse(itemStr);
                        if (item.expiry && now > item.expiry) {
                            localStorage.removeItem(key);
                            cleanedCount++;
                        }
                    }
                } catch (e) {
                    // If there's an error parsing the item, skip it
                    console.warn(`Failed to parse item with key: ${key}`, e);
                }
            }
        }

        return cleanedCount;
    }
}

LocalStorageManager.cleanupExpiredItems();

export default LocalStorageManager;
